import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fetchSessionTimeline, fetchFullSessionTimeline } from '../services/analyticsService';
import { getLogForEvent, getAppLogContent, getLogContent } from '../services/usersService';
import { useError } from '../contexts/ErrorContext';
import { ANALYTICS_TIMEZONE, formatInTimeZone, timeZoneAbbrev } from '../utils/timezone';
import { deriveGogglesConnectivityTimeline } from '../utils/gogglesConnectivityTimeline';
import GogglesConnectivityTimeline from './GogglesConnectivityTimeline';
import LogDownloadFormatModal from './LogDownloadFormatModal';
import { reorderAppLogContent, RAW_APP_LOG_WARNING } from '../utils/mergeAppLog';
import { parseGogglesLog } from '../utils/gogglesLog';
import {
  LOG_LEVELS,
  parseLogPath,
  collectTimedRecords,
  mergeTimelineWithLog,
  collectUnknownTimeRecords,
  countByLevel,
} from '../utils/bleLogOverlay';
import { BleLogOverlayBar, BleLogRun, BleLogUnknownPanel } from './BleLogOverlay';
import './SessionDetailView.css';

// Unchecked together by the "Hide common goggles plumbing events" preset. The preset lists every
// name rather than the ones loaded so far, so types arriving with "Load More" stay hidden.
const GOGGLES_PLUMBING_EVENT_NAMES = [
  'GogglesCharacteristicDiscovered',
  'GogglesDidUpdateValue',
  'GogglesDidWriteValue',
  'GogglesNotificationStateUpdate',
  'GogglesServiceInitialized'
];

/**
 * SessionDetailView - Detailed view of a single analytics session
 * Shows session metadata and full event timeline
 * Displays in the main content area (not a modal)
 */
const SessionDetailView = ({ session, userId, onBack }) => {
  const { addError } = useError();
  // Every time in the session detail view renders in Pacific time, the zone GA4 shards on and
  // the processed goggles log download uses, regardless of the viewer's zone or the page picker.
  const timeZone = ANALYTICS_TIMEZONE;
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedEventTypes, setSelectedEventTypes] = useState(new Set()); // Empty = all selected
  // Unchecked event types whose "Show errors" box is off. Only meaningful while the type is in
  // selectedEventTypes; re-checking a type clears its entry, so the box always restarts at on.
  const [errorsHiddenTypes, setErrorsHiddenTypes] = useState(new Set());
  const [highlightedEventIndex, setHighlightedEventIndex] = useState(null);
  const [showQueryIndex, setShowQueryIndex] = useState(null); // Index of event showing query
  const [copiedQuery, setCopiedQuery] = useState(false);
  const [showSessionQuery, setShowSessionQuery] = useState(false); // Toggle for session query display
  const [logDownloadState, setLogDownloadState] = useState({}); // event index -> { status, filename, source, gapSeconds, tooltip }
  const [logFormatPrompt, setLogFormatPrompt] = useState(null); // { index, event } | null - which row's format-choice modal is open
  const [exportState, setExportState] = useState({ status: 'idle' }); // idle | loading { loaded, total } | error { message }
  // BLE Log Overlay (plans/ble-log-overlay): at most one GogglesLogCaptured row's log laid into
  // the timeline. Keyed by the capture event's eventTimestampMicros, not its row index, because
  // row indices shift whenever the event-type filter changes.
  const [bleOverlay, setBleOverlay] = useState(null); // { eventKey, status: 'loading' | 'ready' | 'error', filename, parsed, error }
  const [bleLevels, setBleLevels] = useState(() => new Set(LOG_LEVELS));
  const [showBleUnknown, setShowBleUnknown] = useState(false);
  const bleRequestRef = useRef(0);
  const filterRef = useRef(null);
  const timelineRef = useRef(null);
  const eventRefs = useRef({});

  // Copy query to clipboard
  const copyQueryToClipboard = async (query) => {
    try {
      await navigator.clipboard.writeText(query);
      setCopiedQuery(true);
      setTimeout(() => setCopiedQuery(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Toggle query display for an event
  const toggleQueryDisplay = (index, e) => {
    e.stopPropagation(); // Don't trigger event expansion
    setShowQueryIndex(showQueryIndex === index ? null : index);
  };

  // Clicking a LogsUploaded row's download button only opens the format-choice prompt.
  // Resolution/download happens in handleSelectLogFormat, once the user picks a format.
  const handleDownloadLogClick = (index, event, e) => {
    e.stopPropagation(); // Don't trigger event expansion
    const currentStatus = logDownloadState[index]?.status;
    if (currentStatus === 'resolving' || currentStatus === 'downloading' || currentStatus === 'reordering') return;
    setLogFormatPrompt({ index, event });
  };

  // Resolve and download the app-log file for the row the format prompt was opened on.
  // format is 'sorted' (post-processed, chronologically interleaved) or 'raw' (as uploaded).
  const handleSelectLogFormat = async (format) => {
    if (!logFormatPrompt) return;
    const { index, event } = logFormatPrompt;

    setLogDownloadState(prev => ({ ...prev, [index]: { status: 'resolving' } }));

    try {
      const result = await getLogForEvent(userId, {
        eventTimestamp: event.timestamp,
        tzOffsetSeconds: event.tzOffsetSeconds,
        pathToLog: event.pathToLog
      }, addError);

      if (!result) {
        setLogDownloadState(prev => ({
          ...prev,
          [index]: { status: 'error', tooltip: 'Failed to resolve log file for this event' }
        }));
        return;
      }

      if (!result.matched) {
        setLogDownloadState(prev => ({
          ...prev,
          [index]: {
            status: 'no-match',
            tooltip: result.reason || 'No matching log file found for this event'
          }
        }));
        return;
      }

      if (format === 'raw') {
        window.alert(RAW_APP_LOG_WARNING);
      }

      setLogDownloadState(prev => ({
        ...prev,
        [index]: { status: format === 'sorted' ? 'reordering' : 'downloading', filename: result.filename, source: result.source, gapSeconds: result.gapSeconds }
      }));

      const content = await getAppLogContent(userId, result.day, result.filename, addError);
      if (content == null) {
        setLogDownloadState(prev => ({
          ...prev,
          [index]: { status: 'error', tooltip: 'Failed to download log file' }
        }));
        return;
      }

      let outputContent = content;
      let outputFilename = result.filename;
      if (format === 'sorted') {
        const reordered = reorderAppLogContent(content);
        if (reordered === null) {
          setLogDownloadState(prev => ({
            ...prev,
            [index]: { status: 'error', tooltip: 'No timestamped entries found in this log file' }
          }));
          return;
        }
        outputContent = reordered;
        outputFilename = result.filename.replace(/\.txt$/, '.sorted.txt');
      }

      const blob = new Blob([outputContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = outputFilename;
      a.click();
      URL.revokeObjectURL(url);

      setLogDownloadState(prev => ({
        ...prev,
        [index]: {
          status: 'done',
          filename: result.filename,
          source: result.source,
          gapSeconds: result.gapSeconds,
          tooltip: result.source === 'path_to_log'
            ? `Exact match: ${result.filename}`
            : `Nearest match: ${result.filename} (${result.gapSeconds >= 0 ? '+' : ''}${result.gapSeconds}s)`
        }
      }));
    } catch (err) {
      console.error('Failed to download log for event:', err);
      setLogDownloadState(prev => ({
        ...prev,
        [index]: { status: 'error', tooltip: err.message || 'Failed to download log file' }
      }));
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };

    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterDropdown]);

  // Format duration for display
  const formatDuration = (ms) => {
    if (!ms) return 'Unknown duration';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds} seconds`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  // Format timestamp for display
  const formatTime = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
      timeZone
    });
  };

  // Format date for display
  const formatDate = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone
    });
  };

  // Load session timeline
  const [loadingMore, setLoadingMore] = useState(false);

  const loadTimeline = useCallback(async () => {
    if (!userId || !session?.sessionId) return;

    setLoading(true);
    try {
      const sessionDate = session.startTime
        ? new Date(session.startTime).toISOString().slice(0, 10)
        : null;

      const result = await fetchSessionTimeline(
        userId,
        session.sessionId,
        sessionDate,
        addError
      );
      setTimeline(result);
    } catch (err) {
      console.error('Failed to load session timeline:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, session, addError]);

  const loadMore = useCallback(async () => {
    if (!userId || !session?.sessionId || !timeline?.hasMore) return;

    setLoadingMore(true);
    try {
      const sessionDate = session.startTime
        ? new Date(session.startTime).toISOString().slice(0, 10)
        : null;

      const result = await fetchSessionTimeline(
        userId,
        session.sessionId,
        sessionDate,
        addError,
        { offset: timeline.offset + timeline.limit, limit: timeline.limit }
      );

      setTimeline(prev => ({
        ...result,
        events: [...prev.events, ...result.events],
      }));
    } catch (err) {
      console.error('Failed to load more events:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [userId, session, addError, timeline]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  const bleEventKey = (event) => String(event.eventTimestampMicros || event.timestamp);

  // The timeline endpoint moves path_to_log out of params into event.pathToLog
  // (backend/routes/analytics.js); params is the fallback for rows shaped otherwise.
  const getCapturePathToLog = (event) => event.pathToLog ?? event.params?.path_to_log;

  // Open, replace or close the single BLE log overlay for a GogglesLogCaptured row.
  // Opening loads every remaining timeline page first, so lines after the loaded events
  // are placed against the session's real last event rather than the last loaded one.
  const handleToggleBleOverlay = async (event, e) => {
    e.stopPropagation(); // Don't trigger event expansion
    const eventKey = bleEventKey(event);
    if (bleOverlay?.eventKey === eventKey) {
      bleRequestRef.current += 1;
      setBleOverlay(null);
      setShowBleUnknown(false);
      return;
    }

    const logPath = parseLogPath(getCapturePathToLog(event));
    if (!logPath) return;

    const requestId = ++bleRequestRef.current;
    setBleOverlay({ eventKey, status: 'loading', filename: logPath.filename });
    setBleLevels(new Set(LOG_LEVELS));
    setShowBleUnknown(false);

    try {
      const sessionDate = session.startTime
        ? new Date(session.startTime).toISOString().slice(0, 10)
        : null;
      const [content, full] = await Promise.all([
        getLogContent(userId, logPath.day, logPath.filename, addError),
        timeline?.hasMore
          ? fetchFullSessionTimeline(userId, session.sessionId, sessionDate)
          : Promise.resolve(null),
      ]);
      if (requestId !== bleRequestRef.current) return;

      if (full) {
        setTimeline(prev => ({ ...prev, events: full.events, hasMore: false }));
      }
      if (content == null) {
        setBleOverlay({ eventKey, status: 'error', filename: logPath.filename, error: 'Failed to download the goggles log' });
        return;
      }
      const parsed = parseGogglesLog(content);
      if (!parsed.hasMarker) {
        setBleOverlay({
          eventKey,
          status: 'error',
          filename: logPath.filename,
          error: 'No "# session=… epoch=…" marker in this log, so its lines cannot be timed',
        });
        return;
      }
      setBleOverlay({ eventKey, status: 'ready', filename: logPath.filename, parsed });
    } catch (err) {
      if (requestId !== bleRequestRef.current) return;
      console.error('Failed to load BLE log overlay:', err);
      addError(err.message || 'Failed to load the goggles log', err.stack, 'Session Detail');
      setBleOverlay({ eventKey, status: 'error', filename: logPath.filename, error: err.message || 'Failed to load the goggles log' });
    }
  };

  const toggleBleLevel = (level) => {
    setBleLevels(prev => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      return next;
    });
  };

  const closeBleOverlay = () => {
    bleRequestRef.current += 1;
    setBleOverlay(null);
    setShowBleUnknown(false);
  };

  // Derived overlay data; recomputed only when the parsed log changes.
  const bleData = useMemo(() => {
    if (bleOverlay?.status !== 'ready') return null;
    const { parsed } = bleOverlay;
    const records = collectTimedRecords(parsed);
    const unknown = collectUnknownTimeRecords(parsed);
    return {
      records,
      unknown,
      levelCounts: countByLevel(records),
      lineCount: records.length + unknown.count,
      sessionsById: new Map(parsed.sessions.map(s => [s.sessionId, s])),
    };
  }, [bleOverlay]);

  // Downloads the whole session (every page of analytics events plus correlated crashes) as JSON.
  // Independent of what the on-screen timeline has loaded or filtered.
  const handleExportJson = useCallback(async () => {
    if (!userId || !session?.sessionId || exportState.status === 'loading') return;

    setExportState({ status: 'loading', loaded: 0, total: null });
    try {
      const sessionDate = session.startTime
        ? new Date(session.startTime).toISOString().slice(0, 10)
        : null;

      const full = await fetchFullSessionTimeline(userId, session.sessionId, sessionDate, {
        onProgress: (loaded, total) => setExportState({ status: 'loading', loaded, total })
      });

      const payload = {
        exportedAt: new Date().toISOString(),
        timeZone,
        uid: userId,
        sessionId: session.sessionId,
        session,
        complete: full.complete,
        totalAnalyticsEvents: full.totalAnalyticsEvents,
        analyticsEventCount: full.analyticsEventCount,
        crashCount: full.crashCount,
        fatalCrashCount: full.fatalCrashCount,
        events: full.events
      };

      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `offboard-session-${userId}-${session.sessionId}.json`;
      a.click();
      URL.revokeObjectURL(url);

      setExportState(full.complete
        ? { status: 'idle' }
        : {
            status: 'error',
            title: 'Export incomplete (file was downloaded)',
            detail: [
              `error: loaded analytics event count does not match the session total`,
              `analyticsEventCount: ${full.analyticsEventCount}`,
              `totalAnalyticsEvents: ${full.totalAnalyticsEvents}`,
              `crashCount: ${full.crashCount}`,
              `uid: ${userId}`,
              `sessionId: ${session.sessionId}`,
              `sessionDate: ${sessionDate}`
            ].join('\n')
          });
    } catch (err) {
      console.error('Failed to export session:', err);
      const ctx = err.exportContext || {};
      const detail = [
        `error: ${err.message || 'Unknown error'}`,
        err.name && err.name !== 'Error' ? `errorType: ${err.name}` : null,
        ctx.endpoint ? `endpoint: POST ${ctx.endpoint}` : null,
        ctx.pageNumber != null ? `page: ${ctx.pageNumber} (offset ${ctx.offset}, limit ${ctx.limit})` : null,
        ctx.loadedAnalyticsEvents != null
          ? `loadedAnalyticsEvents: ${ctx.loadedAnalyticsEvents}${ctx.totalAnalyticsEvents != null ? ` of ${ctx.totalAnalyticsEvents}` : ' (total unknown, first page failed)'}`
          : null,
        ctx.crashesSoFar != null ? `crashesSoFar: ${ctx.crashesSoFar}` : null,
        `uid: ${userId}`,
        `sessionId: ${session.sessionId}`,
        `sessionDate: ${session.startTime ? new Date(session.startTime).toISOString().slice(0, 10) : 'null'}`,
        `time: ${new Date().toISOString()}`,
        err.stack ? `\nstack:\n${err.stack}` : null
      ].filter(Boolean).join('\n');
      setExportState({ status: 'error', title: 'Export failed', detail });
    }
  }, [userId, session, timeZone, exportState.status]);

  // Get unique event types from timeline
  const getUniqueEventTypes = () => {
    if (!timeline?.events) return [];
    const types = new Set(timeline.events.map(e => e.eventName));
    return Array.from(types).sort();
  };

  // Toggle event type filter
  const toggleEventType = (eventType) => {
    setSelectedEventTypes(prev => {
      const next = new Set(prev);
      if (next.has(eventType)) {
        next.delete(eventType);
      } else {
        next.add(eventType);
      }
      return next;
    });
    // Re-checking a type discards its "Show errors" choice; unchecking starts it at on.
    setErrorsHiddenTypes(prev => {
      if (!prev.has(eventType)) return prev;
      const next = new Set(prev);
      next.delete(eventType);
      return next;
    });
  };

  // Toggle "Show errors" for an unchecked event type
  const toggleShowErrors = (eventType) => {
    setErrorsHiddenTypes(prev => {
      const next = new Set(prev);
      if (next.has(eventType)) {
        next.delete(eventType);
      } else {
        next.add(eventType);
      }
      return next;
    });
  };

  // Select all event types
  const selectAllEventTypes = () => {
    setSelectedEventTypes(new Set());
    setErrorsHiddenTypes(new Set());
  };

  // Deselect all event types
  const deselectAllEventTypes = () => {
    setSelectedEventTypes(new Set(getUniqueEventTypes()));
    setErrorsHiddenTypes(new Set());
  };

  const isPlumbingPresetOn = () => GOGGLES_PLUMBING_EVENT_NAMES.every(name => selectedEventTypes.has(name));

  // Hide or restore the plumbing event types. Either way their "Show errors" boxes return to on,
  // so turning the preset on can never hide an error.
  const togglePlumbingPreset = () => {
    const presetOn = isPlumbingPresetOn();
    setSelectedEventTypes(prev => {
      const next = new Set(prev);
      GOGGLES_PLUMBING_EVENT_NAMES.forEach(name => (presetOn ? next.delete(name) : next.add(name)));
      return next;
    });
    setErrorsHiddenTypes(prev => {
      const next = new Set(prev);
      GOGGLES_PLUMBING_EVENT_NAMES.forEach(name => next.delete(name));
      return next;
    });
  };

  // Check if an event type is selected (empty set means all are selected)
  const isEventTypeSelected = (eventType) => {
    if (selectedEventTypes.size === 0) return true;
    return !selectedEventTypes.has(eventType);
  };

  // Get filtered events
  const getFilteredEvents = () => {
    if (!timeline?.events) return [];
    if (selectedEventTypes.size === 0) return timeline.events;
    // An event of an unchecked type stays visible when it is an error and that type's
    // "Show errors" box is on.
    return timeline.events.filter(e =>
      !selectedEventTypes.has(e.eventName) || (e.isError && !errorsHiddenTypes.has(e.eventName))
    );
  };

  // Count active filters
  const getActiveFilterCount = () => {
    return selectedEventTypes.size;
  };

  // Count error events (excluding crashes - they have their own count)
  const getErrorCount = () => {
    if (!timeline?.events) return 0;
    return timeline.events.filter(e => e.isError && !e.isCrash).length;
  };

  // Get error events for summary (including crashes)
  const getErrorEvents = () => {
    if (!timeline?.events) return [];
    return timeline.events.filter(e => e.isError || e.isCrash);
  };

  // Get grouped error counts by event type
  const getGroupedErrors = () => {
    const errorEvents = getErrorEvents();
    const grouped = {};
    
    errorEvents.forEach((event, idx) => {
      if (!grouped[event.eventName]) {
        grouped[event.eventName] = {
          count: 0,
          firstTimestamp: event.timestamp,
          lastTimestamp: event.timestamp,
          firstIndex: idx
        };
      }
      grouped[event.eventName].count++;
      grouped[event.eventName].lastTimestamp = event.timestamp;
    });
    
    // Sort by count descending
    return Object.entries(grouped)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.count - a.count);
  };

  // Filter out null values (backend already filters hidden keys)
  const getDisplayParams = (params) => {
    if (!params) return {};
    const filtered = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value != null) {
        filtered[key] = value;
      }
    });
    return filtered;
  };

  // Jump to first error or crash of a specific type
  const jumpToError = (eventName) => {
    if (!timeline?.events) return;
    
    // Find the first occurrence of this error/crash type in the filtered events
    const filteredEvents = getFilteredEvents();
    const targetIndex = filteredEvents.findIndex(e => e.eventName === eventName && (e.isError || e.isCrash));
    
    if (targetIndex !== -1 && eventRefs.current[targetIndex]) {
      // Scroll to the event
      eventRefs.current[targetIndex].scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
      
      // Highlight it
      setHighlightedEventIndex(targetIndex);
      
      // Expand it if it has params
      const event = filteredEvents[targetIndex];
      const displayParams = getDisplayParams(event.params);
      if (Object.keys(displayParams).length > 0) {
        setExpandedEvents(prev => new Set([...prev, targetIndex]));
      }
      
      // Remove highlight after animation
      setTimeout(() => {
        setHighlightedEventIndex(null);
      }, 2000);
    }
  };

  // Toggle event expansion
  const toggleEventExpansion = (eventIndex) => {
    setExpandedEvents(prev => {
      const next = new Set(prev);
      if (next.has(eventIndex)) {
        next.delete(eventIndex);
      } else {
        next.add(eventIndex);
      }
      return next;
    });
  };

  // Error event names list
  const ERROR_EVENT_NAMES = [
    'GogglesFailedToConnect',
    'GogglesConfigFailedToSync',
    'GogglesForgetFailedToSync',
    'MemoryWarning',
    'GogglesOTAFailed',
    'GogglesCrashRecorded'
  ];

  // Successful goggles serial-log capture — called out in electric lime so it
  // stands apart from every other event color in the timeline.
  const LOG_CAPTURE_EVENT_NAME = 'GogglesLogCaptured';
  const LOG_CAPTURE_COLOR = '#c6ff00';

  // Check if event type is an error
  const isErrorEventType = (eventName) => {
    return ERROR_EVENT_NAMES.includes(eventName);
  };

  // Check if event type is a crash
  const isCrashEventType = (eventName) => {
    return eventName === 'CRASH_FATAL' || eventName === 'CRASH_NON_FATAL';
  };

  // Get event color based on event name and error/crash status
  // Accepts either an event object or an event name string
  const getEventColor = (eventOrName) => {
    // Handle event object
    if (typeof eventOrName === 'object' && eventOrName !== null) {
      // Fatal crashes are dark red/maroon
      if (eventOrName.isCrash && eventOrName.isFatal) return '#b71c1c';
      // Non-fatal crashes are orange-red
      if (eventOrName.isCrash) return '#ff5722';
      // Error events are red
      if (eventOrName.isError) return '#f44336';
      
      const eventName = eventOrName.eventName;
      const colors = {
        'session_start': '#4CAF50',
        'screen_view': '#2196F3',
        'user_engagement': '#9C27B0',
        'first_open': '#FF9800',
        'app_remove': '#f44336',
        'app_update': '#00BCD4',
        'in_app_purchase': '#E91E63',
        'ad_impression': '#795548',
        'GogglesConnected': '#2979ff',
        [LOG_CAPTURE_EVENT_NAME]: LOG_CAPTURE_COLOR,
        'error': '#f44336',
        'crash': '#f44336',
      };
      return colors[eventName] || '#667eea';
    }
    
    // Handle string event name
    const eventName = eventOrName;
    
    // Crash types
    if (eventName === 'CRASH_FATAL') return '#b71c1c';
    if (eventName === 'CRASH_NON_FATAL') return '#ff5722';
    
    // Error event names
    if (isErrorEventType(eventName)) return '#f44336';
    
    const colors = {
      'session_start': '#4CAF50',
      'screen_view': '#2196F3',
      'user_engagement': '#9C27B0',
      'first_open': '#FF9800',
      'app_remove': '#f44336',
      'app_update': '#00BCD4',
      'in_app_purchase': '#E91E63',
      'ad_impression': '#795548',
      'GogglesConnected': '#2979ff',
      [LOG_CAPTURE_EVENT_NAME]: LOG_CAPTURE_COLOR,
      'error': '#f44336',
      'crash': '#f44336',
    };
    return colors[eventName] || '#667eea';
  };

  // GogglesLogCaptured names the window the app asked the goggles to report on:
  // `start_investigate_time` to `responded_at`, both UTC strings like
  // "2026-09-21 00:11:24 +0000". The same two epochs are encoded in
  // `path_to_log` as goggles_<start>_<end>.txt, which is the fallback when a
  // capture is missing either param.
  const parseGogglesUtcParam = (value) => {
    if (typeof value !== 'string') return null;
    const match = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})\s*([+-]\d{2}):?(\d{2})?$/.exec(value.trim());
    if (!match) return null;
    const [, date, time, offsetHours, offsetMinutes = '00'] = match;
    const parsed = new Date(`${date}T${time}${offsetHours}:${offsetMinutes}`);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const epochsFromLogPath = (path) => {
    const match = /goggles_(\d{9,})_(\d{9,})\./.exec(typeof path === 'string' ? path : '');
    if (!match) return [null, null];
    return [new Date(Number(match[1]) * 1000), new Date(Number(match[2]) * 1000)];
  };

  // The investigate window rendered in Pacific time (ANALYTICS_TIMEZONE), the zone
  // the processed goggles log download is written in, regardless of the viewer's
  // zone or the page's selected zone, so the badge and the file show the same clock.
  const getInvestigateWindow = (params) => {
    const [pathStart, pathEnd] = epochsFromLogPath(params.path_to_log);
    const start = parseGogglesUtcParam(params.start_investigate_time) || pathStart;
    const end = parseGogglesUtcParam(params.responded_at) || pathEnd;
    if (!start || !end) return null;

    const clockOptions = { hour: 'numeric', minute: '2-digit', second: '2-digit' };
    return {
      start: formatInTimeZone(start, ANALYTICS_TIMEZONE, clockOptions),
      end: formatInTimeZone(end, ANALYTICS_TIMEZONE, clockOptions),
      zone: timeZoneAbbrev(ANALYTICS_TIMEZONE, start),
      title: `Goggles log window requested by the app, in ${ANALYTICS_TIMEZONE} (same as the chronological log download)`,
    };
  };

  // Map a BLE RSSI reading (dBm) to a signal strength tier for the bars indicator
  const getSignalInfo = (rssi) => {
    if (rssi >= -60) return { bars: 4, label: 'Strong', color: '#4CAF50' };
    if (rssi >= -70) return { bars: 3, label: 'Good', color: '#8BC34A' };
    if (rssi >= -80) return { bars: 2, label: 'Fair', color: '#FFC107' };
    if (rssi >= -90) return { bars: 1, label: 'Weak', color: '#FF9800' };
    return { bars: 0, label: 'Very Weak', color: '#f44336' };
  };

  // Render a small 4-bar signal strength icon, filling `filledCount` bars in `color`
  const renderSignalBars = (filledCount, color) => {
    const heights = [4, 7, 10, 13];
    return (
      <svg width="16" height="14" viewBox="0 0 16 14" className="session-event-rssi-bars-svg">
        {heights.map((h, i) => (
          <rect
            key={i}
            x={i * 4}
            y={13 - h}
            width="3"
            height={h}
            rx="0.5"
            fill={i < filledCount ? color : 'rgba(255, 255, 255, 0.15)'}
          />
        ))}
      </svg>
    );
  };

  // Get crash count
  const getCrashCount = () => {
    if (!timeline?.events) return 0;
    return timeline.events.filter(e => e.isCrash).length;
  };

  // Get fatal crash count
  const getFatalCrashCount = () => {
    if (!timeline?.events) return 0;
    return timeline.events.filter(e => e.isCrash && e.isFatal).length;
  };

  const gogglesConnectivityTimeline = useMemo(
    () => deriveGogglesConnectivityTimeline(timeline?.events),
    [timeline]
  );

  // One GA4/crash event row. `index` is the event's position in getFilteredEvents(), which keys
  // expandedEvents, logDownloadState, eventRefs and the query toggles.
  const renderEventRow = (event, index) => {
    const isExpanded = expandedEvents.has(index);
    const displayParams = getDisplayParams(event.params);
    const hasParams = Object.keys(displayParams).length > 0 || (event.isCrash && event.crashDetails);
    const isError = event.isError || false;
    const isCrash = event.isCrash || false;
    const eventColor = getEventColor(event);
    const isHighlighted = highlightedEventIndex === index;
    const isLogCapture = event.eventName === LOG_CAPTURE_EVENT_NAME && !isError && !isCrash;
    const investigateWindow = isLogCapture ? getInvestigateWindow(displayParams) : null;
    const rssiValue = event.eventName === 'GogglesDisconnected' && displayParams.rssi != null
      ? Number(displayParams.rssi)
      : null;
    const signalInfo = rssiValue != null && !Number.isNaN(rssiValue) ? getSignalInfo(rssiValue) : null;
    const isResetReasonEvent = event.eventName === 'GogglesLastResetReason';
    const uptimeMs = isResetReasonEvent && displayParams.uptimeMilliseconds != null
      ? Number(displayParams.uptimeMilliseconds)
      : null;
    const uptimeSeconds = uptimeMs != null && !Number.isNaN(uptimeMs) ? (uptimeMs / 1000).toFixed(2) : null;
    const resetReasonLabel = isResetReasonEvent ? displayParams.resetReasonInterpreted ?? null : null;
    const resetWasCrash = displayParams.isCrash === 1 || displayParams.isCrash === true;
    const logState = logDownloadState[index] || {};
    const isLogBusy = logState.status === 'resolving' || logState.status === 'downloading' || logState.status === 'reordering';

    return (
      <div
        key={index}
        ref={el => eventRefs.current[index] = el}
        className={`session-timeline-event ${hasParams ? 'has-params' : ''} ${isExpanded ? 'expanded' : ''} ${isError ? 'is-error' : ''} ${isCrash ? 'is-crash' : ''} ${event.isFatal ? 'is-fatal' : ''} ${isHighlighted ? 'highlighted' : ''} ${isLogCapture ? 'is-log-capture' : ''}`}
      >
      <div 
        className="session-event-dot" 
        style={{ backgroundColor: eventColor }}
      />
      <div className="session-event-line" />
      
      <div className="session-event-content">
        <div 
          className="session-event-header"
          onClick={() => hasParams && toggleEventExpansion(index)}
          style={{ cursor: hasParams ? 'pointer' : 'default' }}
        >
          {isCrash && <span className={`session-event-crash-badge ${event.isFatal ? 'fatal' : ''}`}>💥</span>}
          {isError && !isCrash && <span className="session-event-error-badge">⚠</span>}
          <span 
            className={`session-event-name ${isLogCapture ? 'is-log-capture' : ''}`}
            style={{ color: eventColor }}
          >
            {event.eventName}
          </span>
          {investigateWindow && (
            <span className="session-event-investigate-badge" title={investigateWindow.title}>
              <span className="session-event-investigate-label">investigate</span>
              <span>{investigateWindow.start}</span>
              <span className="session-event-investigate-dash">–</span>
              <span>{investigateWindow.end}</span>
              <span className="session-event-investigate-zone">{investigateWindow.zone}</span>
            </span>
          )}
          {isLogCapture && parseLogPath(getCapturePathToLog(event)) && (() => {
            const bleOpen = bleOverlay?.eventKey === bleEventKey(event);
            const bleStatus = bleOpen ? bleOverlay.status : 'idle';
            return (
              <button
                type="button"
                className={`session-ble-log-button status-${bleStatus}`}
                aria-pressed={bleOpen && bleStatus === 'ready'}
                disabled={bleStatus === 'loading'}
                title={bleStatus === 'error' ? bleOverlay.error : "Show this capture's goggles log in the timeline"}
                onClick={(e) => handleToggleBleOverlay(event, e)}
              >
                {bleStatus === 'loading' && 'Loading…'}
                {bleStatus === 'error' && '⚠ BLE log'}
                {bleStatus === 'ready' && 'Hide BLE log'}
                {bleStatus === 'idle' && 'BLE log'}
              </button>
            );
          })()}
          {signalInfo && (
            <span
              className="session-event-rssi-badge"
              title={`RSSI ${rssiValue} dBm — ${signalInfo.label} signal`}
            >
              {renderSignalBars(signalInfo.bars, signalInfo.color)}
              <span className="session-event-rssi-value">{rssiValue} dBm</span>
            </span>
          )}
          {resetReasonLabel && (
            <span
              className={`session-event-reset-badge ${resetWasCrash ? 'crash' : ''}`}
              title={`Reset reason: ${resetReasonLabel}${resetWasCrash ? ' (crash)' : ''}`}
            >
              {resetReasonLabel}
            </span>
          )}
          {uptimeSeconds != null && (
            <span
              className="session-event-uptime-badge"
              title={`Uptime since boot: ${uptimeMs} ms`}
            >
              {uptimeSeconds} s uptime
            </span>
          )}
          {event.eventName === 'LogsUploaded' && (
            <button
              type="button"
              className={`session-log-download-button status-${logState.status || 'idle'}`}
              onClick={(e) => handleDownloadLogClick(index, event, e)}
              disabled={isLogBusy}
              title={logState.tooltip || 'Download the uploaded log file'}
            >
              {isLogBusy && <span className="session-log-download-spinner" />}
              {logState.status === 'reordering' && (
                <span className="session-log-download-label">Reordering…</span>
              )}
              {!isLogBusy && logState.status === 'no-match' && (
                <span className="session-log-download-icon">⚠</span>
              )}
              {!isLogBusy && logState.status === 'error' && (
                <span className="session-log-download-icon">✕</span>
              )}
              {!isLogBusy && logState.status !== 'no-match' && logState.status !== 'error' && (
                <span className="session-log-download-icon">⬇</span>
              )}
              {logState.status === 'done' && (
                <span className="session-log-download-label">
                  {logState.filename}
                  {logState.source === 'nearest' && ` (${logState.gapSeconds >= 0 ? '+' : ''}${logState.gapSeconds}s)`}
                </span>
              )}
            </button>
          )}
          <span className="session-event-time">
            {formatTime(event.timestamp)}
          </span>
          {hasParams && (
            <span className={`session-event-expand ${isExpanded ? 'expanded' : ''}`}>
              ▶
            </span>
          )}
        </div>

        {isExpanded && (hasParams || isCrash) && (
          <div 
            className="session-event-params"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Crash Details Section */}
            {isCrash && event.crashDetails && (
              <div className="crash-details-section">
                <div className="crash-details-header">
                  <span className="crash-details-icon">🔍</span>
                  <span className="crash-details-title">Crash Details</span>
                </div>
                {event.crashDetails.issueTitle && (
                  <div className="session-param-row crash-param">
                    <span className="session-param-key">Issue</span>
                    <span className="session-param-value crash-issue-title">
                      {event.crashDetails.issueTitle}
                    </span>
                  </div>
                )}
                {event.crashDetails.issueSubtitle && (
                  <div className="session-param-row crash-param">
                    <span className="session-param-key">Details</span>
                    <span className="session-param-value">
                      {event.crashDetails.issueSubtitle}
                    </span>
                  </div>
                )}
                {event.crashDetails.errorType && (
                  <div className="session-param-row crash-param">
                    <span className="session-param-key">Type</span>
                    <span className="session-param-value">
                      {event.crashDetails.errorType}
                    </span>
                  </div>
                )}
                {event.crashDetails.blameFrame && (
                  <div className="session-param-row crash-param">
                    <span className="session-param-key">Blame</span>
                    <span className="session-param-value crash-blame">
                      {event.crashDetails.blameFrame.symbol}
                      {event.crashDetails.blameFrame.file && (
                        <span className="crash-file">
                          {' '}at {event.crashDetails.blameFrame.file}
                          {event.crashDetails.blameFrame.line && `:${event.crashDetails.blameFrame.line}`}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                {event.crashDetails.processState && (
                  <div className="session-param-row crash-param">
                    <span className="session-param-key">State</span>
                    <span className="session-param-value">
                      {event.crashDetails.processState}
                    </span>
                  </div>
                )}
                
                {/* Custom Keys Section */}
                {event.crashDetails.customKeys && Object.keys(event.crashDetails.customKeys).length > 0 && (
                  <div className="crash-custom-keys-section">
                    <div className="crash-custom-keys-header">
                      <span className="crash-custom-keys-icon">🔑</span>
                      <span className="crash-custom-keys-title">Custom Keys</span>
                    </div>
                    <div className="crash-custom-keys-list">
                      {Object.entries(event.crashDetails.customKeys).map(([key, value]) => (
                        <div key={key} className="session-param-row crash-param custom-key-row">
                          <span className="session-param-key">{key}</span>
                          <span className="session-param-value custom-key-value">
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Show Query Button */}
                {event.crashDetails.debugQuery && (
                  <div className="crash-query-section">
                    <button 
                      className="crash-query-button"
                      onClick={(e) => toggleQueryDisplay(index, e)}
                    >
                      {showQueryIndex === index ? '🔽 Hide Query' : '🔍 Show Query'}
                    </button>
                    
                    {showQueryIndex === index && (
                      <div className="crash-query-display">
                        <div className="crash-query-header">
                          <span>BigQuery SQL</span>
                          <button 
                            className="crash-query-copy"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyQueryToClipboard(event.crashDetails.debugQuery);
                            }}
                          >
                            {copiedQuery ? '✓ Copied!' : '📋 Copy'}
                          </button>
                        </div>
                        <pre className="crash-query-code">
                          {event.crashDetails.debugQuery}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {/* Regular Params */}
            {Object.entries(displayParams).map(([key, value]) => (
              <div key={key} className="session-param-row">
                <span className="session-param-key">{key}</span>
                <span className="session-param-value">
                  {typeof value === 'object' 
                    ? JSON.stringify(value) 
                    : String(value)}
                </span>
              </div>
            ))}
            
            {/* Event Query Button (for non-crash events) */}
            {!isCrash && event.debugQuery && (
              <div className="event-query-section">
                <button 
                  className="event-query-button"
                  onClick={(e) => toggleQueryDisplay(index, e)}
                >
                  {showQueryIndex === index ? '🔽 Hide Query' : '🔍 Show Query'}
                </button>
                
                {showQueryIndex === index && (
                  <div className="event-query-display">
                    <div className="event-query-header">
                      <span>BigQuery SQL</span>
                      <button 
                        className="event-query-copy"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyQueryToClipboard(event.debugQuery);
                        }}
                      >
                        {copiedQuery ? '✓ Copied!' : '📋 Copy'}
                      </button>
                    </div>
                    <pre className="event-query-code">
                      {event.debugQuery}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!isExpanded && hasParams && (
          <div className="session-event-preview">
            {Object.entries(displayParams).slice(0, 3).map(([key, value]) => (
              <span key={key} className="session-param-chip">
                {key}: {String(value).substring(0, 40)}
              </span>
            ))}
            {Object.keys(displayParams).length > 3 && (
              <span className="session-param-more">
                +{Object.keys(displayParams).length - 3} more
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
  };

  const filteredEvents = getFilteredEvents();
  const bleMerged = bleData ? mergeTimelineWithLog(filteredEvents, bleData.records, bleLevels) : null;
  const timelineItems = bleMerged
    ? bleMerged.items
    : filteredEvents.map((event, index) => ({ kind: 'event', event, index }));

  if (!session) return null;

  return (
    <div className="session-detail-page">
      {/* Header with back button and filter */}
      <div className="session-detail-header">
        <button className="session-back-button" onClick={onBack}>
          ← Back to Sessions
        </button>
        <div className="session-detail-header-content">
          <h2 className="session-detail-title">Session Details</h2>
          <p className="session-detail-date">{formatDate(session.startTime)}</p>
        </div>
        
        <button
          className={`session-export-button ${exportState.status === 'error' ? 'has-error' : ''}`}
          onClick={handleExportJson}
          disabled={exportState.status === 'loading' || loading}
          title="Download the full session (all events and crashes) as JSON"
        >
          <span className="export-icon">⬇</span>
          {exportState.status === 'loading'
            ? `Exporting… ${exportState.loaded}${exportState.total != null ? ` / ${exportState.total}` : ''}`
            : exportState.status === 'error'
              ? 'Export failed, retry'
              : 'Export JSON'}
        </button>

        {/* Session Query Button */}
        {timeline?.debugQuery && (
          <button 
            className={`session-query-toggle ${showSessionQuery ? 'active' : ''}`}
            onClick={() => setShowSessionQuery(!showSessionQuery)}
          >
            <span className="query-icon">🔍</span>
            {showSessionQuery ? 'Hide Query' : 'Show Query'}
          </button>
        )}
        
        <div className="session-filter-container" ref={filterRef}>
          <button 
            className={`session-filter-button ${showFilterDropdown ? 'active' : ''} ${getActiveFilterCount() > 0 ? 'has-filters' : ''}`}
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
          >
            <span className="filter-icon">⚙</span>
            Filter
            {getActiveFilterCount() > 0 && (
              <span className="filter-badge">{getActiveFilterCount()}</span>
            )}
          </button>
          
          {showFilterDropdown && (
            <div className="session-filter-dropdown">
              <div className="filter-section">
                <div className="filter-section-header">
                  <span className="filter-section-title">Presets</span>
                </div>
                <label className="filter-option">
                  <input
                    type="checkbox"
                    checked={isPlumbingPresetOn()}
                    onChange={togglePlumbingPreset}
                  />
                  <span className="filter-preset-label">Hide common goggles plumbing events</span>
                  <span className="filter-option-count">
                    {timeline?.events?.filter(e => GOGGLES_PLUMBING_EVENT_NAMES.includes(e.eventName)).length || 0}
                  </span>
                </label>
              </div>
              <div className="filter-section">
                <div className="filter-section-header">
                  <span className="filter-section-title">Event Type</span>
                  <div className="filter-section-actions">
                    <button 
                      className="filter-action-btn"
                      onClick={selectAllEventTypes}
                    >
                      All
                    </button>
                    <button 
                      className="filter-action-btn"
                      onClick={deselectAllEventTypes}
                    >
                      None
                    </button>
                  </div>
                </div>
                <div className="filter-options">
                  {getUniqueEventTypes().map(eventType => {
                    const isError = isErrorEventType(eventType);
                    const isCrash = isCrashEventType(eventType);
                    const isFatal = eventType === 'CRASH_FATAL';
                    const count = timeline?.events?.filter(e => e.eventName === eventType).length || 0;
                    const typeSelected = isEventTypeSelected(eventType);

                    return (
                      <React.Fragment key={eventType}>
                        <label
                          className={`filter-option ${isError ? 'filter-option-error' : ''} ${isCrash ? 'filter-option-crash' : ''} ${isFatal ? 'filter-option-fatal' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={typeSelected}
                            onChange={() => toggleEventType(eventType)}
                          />
                          <span
                            className={`filter-option-dot ${isCrash ? 'crash-dot' : ''}`}
                            style={{ backgroundColor: getEventColor(eventType) }}
                          />
                          {isCrash && <span className="filter-option-icon">💥</span>}
                          {isError && !isCrash && <span className="filter-option-icon">⚠️</span>}
                          <span className="filter-option-label">{eventType}</span>
                          <span className={`filter-option-count ${isError || isCrash ? 'error-count' : ''}`}>
                            {count}
                          </span>
                        </label>
                        {!typeSelected && (
                          <label className="filter-suboption">
                            <input
                              type="checkbox"
                              checked={!errorsHiddenTypes.has(eventType)}
                              onChange={() => toggleShowErrors(eventType)}
                            />
                            <span className="filter-suboption-label">Show errors</span>
                          </label>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Export error detail */}
      {exportState.status === 'error' && (
        <div className="session-query-container session-export-error">
          <div className="session-query-header">
            <span className="session-query-title">{exportState.title}</span>
            <span className="session-export-error-actions">
              <button
                className="session-query-copy-button"
                onClick={() => copyQueryToClipboard(exportState.detail)}
              >
                {copiedQuery ? '✓ Copied!' : '📋 Copy'}
              </button>
              <button
                className="session-query-copy-button"
                onClick={() => setExportState({ status: 'idle' })}
              >
                Dismiss
              </button>
            </span>
          </div>
          <pre className="session-query-code">{exportState.detail}</pre>
        </div>
      )}

      {/* Session Query Display */}
      {showSessionQuery && timeline?.debugQuery && (
        <div className="session-query-container">
          <div className="session-query-header">
            <span className="session-query-title">📋 BigQuery SQL</span>
            <button 
              className="session-query-copy-button"
              onClick={() => copyQueryToClipboard(timeline.debugQuery)}
            >
              {copiedQuery ? '✓ Copied!' : '📋 Copy'}
            </button>
          </div>
          <pre className="session-query-code">{timeline.debugQuery}</pre>
        </div>
      )}

      {/* Session Summary Cards */}
      <div className="session-detail-summary">
        <div className="session-summary-card">
          <span className="session-summary-label">Time Range</span>
          <span className="session-summary-value">
            {formatTime(session.startTime)} – {formatTime(session.endTime)}
          </span>
        </div>
        <div className="session-summary-card">
          <span className="session-summary-label">Duration</span>
          <span className="session-summary-value">
            {formatDuration(session.durationMs)}
          </span>
        </div>
        <div className="session-summary-card">
          <span className="session-summary-label">Device</span>
          <span className="session-summary-value">
            {session.device?.os || 'Unknown'} • {session.device?.category || 'Unknown'}
          </span>
        </div>
        <div className="session-summary-card">
          <span className="session-summary-label">App Version</span>
          <span className="session-summary-value">
            {session.appVersion || 'Unknown'}
          </span>
        </div>
        <div className="session-summary-card">
          <span className="session-summary-label">Session ID</span>
          <span className="session-summary-value session-id">
            {session.sessionId}
          </span>
        </div>
        <div className="session-summary-card">
          <span className="session-summary-label">Total Events</span>
          <span className="session-summary-value">
            {timeline?.totalEvents || session.eventCount || 0}
          </span>
        </div>
        <div className={`session-summary-card ${getErrorCount() > 0 ? 'error-card' : ''}`}>
          <span className="session-summary-label">Errors</span>
          <span className={`session-summary-value ${getErrorCount() > 0 ? 'error-value' : ''}`}>
            {getErrorCount()}
          </span>
        </div>
        <div className={`session-summary-card ${getCrashCount() > 0 ? 'crash-card' : ''}`}>
          <span className="session-summary-label">Crashes</span>
          <span className={`session-summary-value ${getCrashCount() > 0 ? 'crash-value' : ''}`}>
            {getCrashCount()}
            {getFatalCrashCount() > 0 && (
              <span className="fatal-indicator"> ({getFatalCrashCount()} fatal)</span>
            )}
          </span>
        </div>
      </div>

      {/* Crash Summary Panel - only shown if there are crashes */}
      {!loading && getCrashCount() > 0 && (
        <div className="session-error-summary crash-summary">
          <div className="error-summary-header crash-header">
            <span className="error-summary-icon">💥</span>
            <span className="error-summary-title">{getCrashCount()} Crash{getCrashCount() !== 1 ? 'es' : ''} Detected</span>
            {getFatalCrashCount() > 0 && (
              <span className="fatal-crash-badge">{getFatalCrashCount()} fatal</span>
            )}
          </div>
          <div className="error-summary-list">
            {getGroupedErrors().filter(g => g.name === 'CRASH_FATAL' || g.name === 'CRASH_NON_FATAL').map((group, idx) => (
              <div 
                key={idx} 
                className={`error-summary-item crash-item clickable ${group.name === 'CRASH_FATAL' ? 'fatal' : ''}`}
                onClick={() => jumpToError(group.name)}
              >
                <span className="error-summary-count">{group.count}×</span>
                <span className="error-summary-name">{group.name === 'CRASH_FATAL' ? 'Fatal Crash' : 'Non-Fatal Crash'}</span>
                <span className="error-summary-time">
                  {group.count === 1 
                    ? formatTime(group.firstTimestamp)
                    : `${formatTime(group.firstTimestamp)} – ${formatTime(group.lastTimestamp)}`
                  }
                </span>
                <span className="error-summary-jump">Jump ↓</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Summary Panel - only shown if there are errors */}
      {!loading && getErrorCount() > 0 && (
        <div className="session-error-summary">
          <div className="error-summary-header">
            <span className="error-summary-icon">⚠</span>
            <span className="error-summary-title">{getErrorCount()} Error{getErrorCount() !== 1 ? 's' : ''} Detected</span>
          </div>
          <div className="error-summary-list">
            {getGroupedErrors().filter(g => g.name !== 'CRASH_FATAL' && g.name !== 'CRASH_NON_FATAL').map((group, idx) => (
              <div 
                key={idx} 
                className="error-summary-item clickable"
                onClick={() => jumpToError(group.name)}
              >
                <span className="error-summary-count">{group.count}×</span>
                <span className="error-summary-name">{group.name}</span>
                <span className="error-summary-time">
                  {group.count === 1 
                    ? formatTime(group.firstTimestamp)
                    : `${formatTime(group.firstTimestamp)} – ${formatTime(group.lastTimestamp)}`
                  }
                </span>
                <span className="error-summary-jump">Jump ↓</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Goggles Connectivity Timeline — synthesized panel, see plans/goggles-connectivity-timeline/ */}
      {!loading && (
        <GogglesConnectivityTimeline
          data={gogglesConnectivityTimeline}
          sessionStartTime={session.startTime}
          sessionEndTime={session.endTime}
          timeZone={timeZone}
        />
      )}

      {/* Timeline Section */}
      <div className="session-detail-timeline">
        <h3 className="session-timeline-title">Event Timeline</h3>

        {loading && (
          <div className="session-timeline-loading">
            <div className="session-spinner"></div>
            <span>Loading timeline...</span>
          </div>
        )}

        {!loading && timeline?.message && (
          <div className="session-timeline-message">
            <span className="session-message-icon">ℹ️</span>
            {timeline.message}
          </div>
        )}

        {!loading && timeline?.events && timeline.events.length > 0 && (
          <>
            {/* Filter status */}
            {getActiveFilterCount() > 0 && (
              <div className="session-filter-status">
                Showing {getFilteredEvents().length} of {timeline.events.length} events
                <button 
                  className="session-filter-clear"
                  onClick={selectAllEventTypes}
                >
                  Clear filters
                </button>
              </div>
            )}
            
            {bleData && (
              <BleLogOverlayBar
                filename={bleOverlay.filename}
                lineCount={bleData.lineCount}
                levelCounts={bleData.levelCounts}
                levels={bleLevels}
                onToggleLevel={toggleBleLevel}
                unknownCount={bleData.unknown.count}
                onOpenUnknown={() => setShowBleUnknown(true)}
                onClose={closeBleOverlay}
              />
            )}
            <div className="session-timeline-events" ref={timelineRef}>
              {bleMerged && bleMerged.before.length > 0 && (
                <BleLogRun records={bleMerged.before} timeZone={timeZone} sessionsById={bleData.sessionsById} variant="before" />
              )}
              {timelineItems.map((item) => (
                item.kind === 'event'
                  ? renderEventRow(item.event, item.index)
                  : (
                    <BleLogRun
                      key={`ble-${item.records[0].sourceLine}`}
                      records={item.records}
                      timeZone={timeZone}
                      sessionsById={bleData.sessionsById}
                    />
                  )
              ))}
              {bleMerged && bleMerged.after.length > 0 && (
                <BleLogRun records={bleMerged.after} timeZone={timeZone} sessionsById={bleData.sessionsById} variant="after" />
              )}
            </div>
          </>
        )}

        {!loading && timeline?.hasMore && (
          <div className="session-timeline-load-more">
            <button
              className="session-load-more-btn"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : `Load More (${timeline.events.length} of ${timeline.totalEvents})`}
            </button>
          </div>
        )}

        {!loading && (!timeline?.events || timeline.events.length === 0) && !timeline?.message && (
          <div className="session-timeline-empty">
            <span className="session-empty-icon">📭</span>
            <p>No events found for this session</p>
          </div>
        )}
      </div>

      <BleLogUnknownPanel
        isOpen={showBleUnknown && Boolean(bleData)}
        onClose={() => setShowBleUnknown(false)}
        groups={bleData?.unknown}
      />

      <LogDownloadFormatModal
        isOpen={logFormatPrompt !== null}
        onClose={() => setLogFormatPrompt(null)}
        onSelectFormat={handleSelectLogFormat}
      />
    </div>
  );
};

export default SessionDetailView;
